import React, { useEffect, useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { normalizeUnitOfMeasure } from '@shared/quantity';
import { OperationalFilterSegmentedRow } from '../../shared/filters/OperationalFilterPrimitives';
import { ticketColors } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { formatEditableMoneyAmount, parseDecimalInput } from '../../shared/numeric/localeNumeric';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { updateProductAvgCost } from '../infra/productsRepo';
import { IVA_RATE_OPTIONS, spanishIvaRateLabel } from '../../fiscal/domain-api';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { catalogProductFormStyles as styles, UOM_OPTIONS } from './catalogProductForm.styles';
import { ProductImagePicker } from './ProductImagePicker';
import { CatalogProductFormSuggestion } from './CatalogProductFormSuggestion';
import type { CatalogProductFormFieldsProps } from './catalogProductFormFields.types';
import type { ProductDraft } from '../domain/productDraft';
import { NEGATIVE_PRICE_MESSAGE, rawPriceInputHadMinusSign } from '../domain/priceFieldGuard';

type Props = Pick<
  CatalogProductFormFieldsProps,
  | 'formMode'
  | 'formSuggestion'
  | 'draft'
  | 'onPriceChange'
  | 'allowIvaRate'
  | 'weightUomEnabled'
  | 'onIvaRateChange'
  | 'hideStockPanels'
  | 'editingProduct'
  | 'currentBranchName'
  | 'currentBranchId'
  | 'editingProductStockLoading'
  | 'editingProductStockFailed'
  | 'onRetryEditingProductStock'
  | 'editingProductCost'
  | 'canEditCost'
  | 'editingProductCurrentMarginValue'
  | 'editingProductCurrentMarginPercent'
  | 'editingProductSuggestedPrice'
  | 'currencyFormatter'
  | 'priceMarginPercentInput'
  | 'onPriceMarginPercentChange'
  | 'onApplySuggestedPrice'
  | 'priceValidationMessage'
  | 'onUnitOfMeasureChange'
  | 'onOpenUomPicker'
  | 'mobile'
  | 'desktopRail'
  | 'desktopQuickMode'
  | 'imageUrl'
  | 'onPickImage'
  | 'onRemoveImage'
  | 'imageUploading'
> & {
  compact?: boolean;
};

const SERVICE_UOM_OPTIONS = UOM_OPTIONS.filter((opt) => opt.id === 'unit' || opt.id === 'hour');

export function CatalogProductFormPricingFields({
  formMode,
  formSuggestion,
  draft,
  onPriceChange,
  allowIvaRate = false,
  weightUomEnabled = false,
  onIvaRateChange,
  hideStockPanels = false,
  editingProduct,
  currentBranchName,
  currentBranchId,
  editingProductStockLoading,
  editingProductStockFailed = false,
  onRetryEditingProductStock,
  editingProductCost,
  canEditCost = false,
  editingProductCurrentMarginValue,
  editingProductCurrentMarginPercent,
  editingProductSuggestedPrice,
  currencyFormatter,
  priceMarginPercentInput,
  onPriceMarginPercentChange,
  onApplySuggestedPrice,
  priceValidationMessage,
  onUnitOfMeasureChange,
  mobile = false,
  desktopRail = false,
  desktopQuickMode = false,
  compact = false,
  onOpenUomPicker,
  imageUrl,
  onPickImage,
  onRemoveImage,
  imageUploading,
}: Props) {
  // Costo/margen vienen del stock (ingreso); solo en edición.
  const isComboParent = editingProduct?.isCombo === true;
  const showStockPanels =
    formMode === 'edit' && !hideStockPanels && draft.itemType !== 'service' && !isComboParent;

  // ── Edición de costo promedio (command server-authoritative) ──
  const editingProductId = editingProduct?.id ?? null;
  const [costInput, setCostInput] = useState('');
  const [costSaving, setCostSaving] = useState(false);
  useEffect(() => {
    setCostInput(editingProductCost != null ? formatEditableMoneyAmount(editingProductCost) : '');
  }, [editingProductId, editingProductCost]);

  const parsedCostInput = parseDecimalInput(costInput);
  const costInputDirty =
    parsedCostInput != null && parsedCostInput !== (editingProductCost ?? 0);
  const canSaveCost =
    !!editingProductId && canEditCost && parsedCostInput != null && parsedCostInput > 0 && costInputDirty && !costSaving;

  const saveEditingProductCost = async () => {
    if (!editingProductId || parsedCostInput == null) return;
    setCostSaving(true);
    try {
      await updateProductAvgCost(editingProductId, parsedCostInput);
      showSuccess('Costo actualizado', 'El costo promedio se actualizó correctamente.');
      onRetryEditingProductStock?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo actualizar el costo.';
      showError('No pudimos guardar el costo', message);
    } finally {
      setCostSaving(false);
    }
  };

  // El sanitizador decimalMoney descarta '-' en silencio ("-500" → 500).
  // Detectamos el signo en el texto crudo (onChange ve el valor sin sanear)
  // para avisar en vez de guardar el valor absoluto sin que el usuario lo note.
  const [priceHadMinusSign, setPriceHadMinusSign] = useState(false);
  const [costHadMinusSign, setCostHadMinusSign] = useState(false);

  const serviceCatalogEnabled = useHasBusinessCapability('service_catalog');
  const isService = draft.itemType === 'service' && serviceCatalogEnabled;
  const showUom = draft.itemType === 'physical' || isService;
  const physicalUomOptions = weightUomEnabled
    ? UOM_OPTIONS
    : UOM_OPTIONS.filter((opt) => opt.id !== 'kg' && opt.id !== 'g');
  const uomOptions = isService ? SERVICE_UOM_OPTIONS : physicalUomOptions;
  const uomSegments = uomOptions.map((opt) => ({ value: opt.id, label: opt.label }));
  const rawUom = normalizeUnitOfMeasure(draft.unitOfMeasure);
  const uomValue = isService && rawUom !== 'unit' && rawUom !== 'hour' ? 'unit' : rawUom;
  const lockedWeightUom =
    draft.itemType === 'physical' && !weightUomEnabled && (rawUom === 'kg' || rawUom === 'g');

  // Services have only 2 UoM options → segmented row is fine.
  // Physical products have 8 → picker trigger opens a sheet (no wall of chips).
  const usePicker = uomOptions.length > 3 && onOpenUomPicker;
  const uomLabel = UOM_OPTIONS.find((opt) => opt.id === uomValue)?.label ?? 'Unidad';

  const priceField = (
    <View style={styles.ticketFormField}>
      <Text style={styles.ticketFormLabel}>
        Precio de venta <Text style={styles.requiredAsterisk}>*</Text>
      </Text>
      <NumericTextInput
        kind="decimalMoney"
        style={styles.ticketFormInput}
        value={draft.price}
        onChangeText={onPriceChange}
        onChange={(e) => setPriceHadMinusSign(rawPriceInputHadMinusSign(e.nativeEvent.text))}
        placeholder="Ej: 1500 o 1500,50"
        placeholderTextColor={ticketColors.ink3}
        testID="catalog.form.price"
      />
      {priceHadMinusSign ? (
        <Text style={[styles.inputHelper, styles.inputHelperError]}>{NEGATIVE_PRICE_MESSAGE}</Text>
      ) : priceValidationMessage ? (
        <Text style={[styles.inputHelper, styles.inputHelperError]}>{priceValidationMessage}</Text>
      ) : null}
    </View>
  );

  const suggestionCard = <CatalogProductFormSuggestion formSuggestion={formSuggestion} />;
  const compactImage = onPickImage && onRemoveImage ? (
    <ProductImagePicker
      compact
      imageUrl={imageUrl}
      onPickImage={onPickImage}
      onRemoveImage={onRemoveImage}
      uploading={imageUploading}
    />
  ) : null;

  const uomField = showUom ? (
    <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
      <Text style={styles.ticketFormLabel}>Unidad de medida</Text>
      {lockedWeightUom ? (
        <>
          <View style={[styles.ticketFormInput, styles.pickerTrigger, posUi.disabledCard]}>
            <Text style={styles.pickerTriggerText} numberOfLines={1}>
              {uomLabel}
            </Text>
          </View>
          <Text style={styles.ticketFormHint}>
            Activá «Vendés por kilo» en Funciones del negocio para cambiar esta unidad.
          </Text>
        </>
      ) : usePicker ? (
        <TouchableOpacity
          style={[styles.ticketFormInput, styles.pickerTrigger, styles.pickerTriggerActive]}
          onPress={onOpenUomPicker}
          accessibilityRole="button"
          accessibilityLabel="Elegir unidad de medida"
          testID="catalog.form.uom.open"
        >
          <Text style={styles.pickerTriggerText} numberOfLines={1}>
            {uomLabel}
          </Text>
          <Text style={styles.pickerTriggerChev}>▾</Text>
        </TouchableOpacity>
      ) : (
        <OperationalFilterSegmentedRow
          options={uomSegments}
          value={uomValue}
          onChange={(id) => onUnitOfMeasureChange?.(id)}
          ticketTone
        />
      )}
    </View>
  ) : null;
  const ivaField = allowIvaRate && onIvaRateChange ? (
    <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
      <Text style={styles.ticketFormLabel}>Alícuota IVA</Text>
      <OperationalFilterSegmentedRow
        options={IVA_RATE_OPTIONS.map((opt) => ({ value: opt.id, label: opt.label }))}
        value={draft.ivaRate}
        onChange={(id) => onIvaRateChange(id as ProductDraft['ivaRate'])}
        ticketTone
      />
      <Text style={posUi.checkoutHint}>
        Seleccionado: {spanishIvaRateLabel(draft.ivaRate)} (responsable inscripto).
      </Text>
    </View>
  ) : null;

  if (compact) {
    return (
      <View style={styles.ticketFormSection}>
        {!desktopQuickMode ? <Text style={styles.ticketFormSectionTitle}>Precio</Text> : null}
        {desktopQuickMode && compactImage ? (
          <View style={styles.quickPriceImageRow}>
            <View style={styles.fieldGridItem}>{priceField}</View>
            {compactImage}
          </View>
        ) : (
          priceField
        )}
        {!desktopQuickMode ? suggestionCard : null}
      </View>
    );
  }

  return (
    <View style={styles.ticketFormSection}>
      {!desktopRail ? <Text style={styles.ticketFormSectionTitle}>Precio y stock</Text> : null}
      {mobile ? (
        <>
          {priceField}
          {uomField}
          {ivaField}
        </>
      ) : (
        <View style={styles.fieldGrid}>
          <View style={styles.fieldGridItem}>{priceField}</View>
          {uomField}
          {ivaField}
        </View>
      )}
      {suggestionCard}
      {formMode !== 'edit' && draft.itemType === 'physical' ? (
        <Text style={styles.ticketFormHint}>El stock se carga en Compras.</Text>
      ) : null}
      {formMode === 'edit' && isComboParent ? (
        <View style={posUi.paymentCard}>
          <Text style={styles.blockTitle}>Combo</Text>
          <Text style={posUi.checkoutHint}>
            El costo de referencia sale de las partes. Editá la composición en la ficha (tab Composición)
            o en este form. El stock lo movés por componente.
          </Text>
          {editingProductCost != null ? (
            <Text style={styles.costMetricValue}>
              Costo ref. {currencyFormatter.format(editingProductCost)}
            </Text>
          ) : (
            <Text style={posUi.checkoutHint}>Costo ref. incompleto — cargá costo en las partes.</Text>
          )}
        </View>
      ) : null}
      {showStockPanels && editingProduct ? (
        <View style={posUi.paymentCard}>
          <View style={styles.costCardHeader}>
            <Text style={styles.blockTitle}>Costo y precio sugerido</Text>
            <Text style={posUi.checkoutHint}>{currentBranchName || 'Sucursal actual'}</Text>
          </View>
          {!currentBranchId ? (
            <Text style={posUi.checkoutHint}>
              Seleccioná una sucursal para cargar el costo y sugerir precio.
            </Text>
          ) : editingProductStockLoading ? (
            <Text style={posUi.checkoutHint}>Cargando costo promedio…</Text>
          ) : editingProductStockFailed ? (
            <>
              <Text style={posUi.checkoutHint}>
                No pudimos verificar el costo en esta sucursal. Reintentá para ver el margen.
              </Text>
              {onRetryEditingProductStock ? (
                <TouchableOpacity
                  style={styles.createMasterLink}
                  onPress={onRetryEditingProductStock}
                  accessibilityRole="button"
                  accessibilityLabel="Reintentar carga del costo"
                >
                  <Text style={styles.createMasterLinkText}>Reintentar</Text>
                </TouchableOpacity>
              ) : null}
            </>
          ) : (
            <>
              <View style={styles.costMetricsRow}>
                <View style={styles.costMetric}>
                  <Text style={styles.costMetricLabel}>Costo promedio</Text>
                  <Text style={styles.costMetricValue}>
                    {editingProductCost != null
                      ? currencyFormatter.format(editingProductCost)
                      : 'Sin cargar'}
                  </Text>
                </View>
                <View style={styles.costMetric}>
                  <Text style={styles.costMetricLabel}>Margen actual</Text>
                  <Text
                    style={[
                      styles.costMetricValue,
                      editingProductCurrentMarginValue != null && editingProductCurrentMarginValue < 0
                        ? styles.costMetricValueNegative
                        : styles.costMetricValuePositive,
                    ]}
                  >
                    {editingProductCurrentMarginValue != null
                      ? currencyFormatter.format(editingProductCurrentMarginValue)
                      : '—'}
                  </Text>
                  <Text style={posUi.checkoutHint}>
                    {editingProductCurrentMarginPercent != null
                      ? `${editingProductCurrentMarginPercent.toFixed(1)}%`
                      : '—'}
                  </Text>
                </View>
              </View>
              {canEditCost ? (
                <View style={styles.ticketFormField}>
                  <Text style={styles.ticketFormLabel}>Costo promedio</Text>
                  <View style={styles.ticketInpRow}>
                    <NumericTextInput
                      kind="decimalMoney"
                      style={[styles.ticketFormInput, styles.ticketInpRowInput]}
                      value={costInput}
                      onChangeText={setCostInput}
                      onChange={(e) => setCostHadMinusSign(rawPriceInputHadMinusSign(e.nativeEvent.text))}
                      placeholder="Ej: 800 o 800,50"
                      placeholderTextColor={ticketColors.ink3}
                      testID="catalog.form.cost"
                    />
                    <TouchableOpacity
                      style={[
                        posUi.primaryButton,
                        styles.suggestButton,
                        !canSaveCost && posUi.disabledCard,
                      ]}
                      onPress={() => void saveEditingProductCost()}
                      disabled={!canSaveCost}
                      accessibilityRole="button"
                      accessibilityLabel="Guardar costo promedio"
                    >
                      <Text style={posUi.primaryButtonText}>
                        {costSaving ? 'Guardando…' : 'Guardar costo'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  {costHadMinusSign ? (
                    <Text style={[styles.inputHelper, styles.inputHelperError]}>{NEGATIVE_PRICE_MESSAGE}</Text>
                  ) : null}
                  {editingProductCost == null ? (
                    <Text style={styles.inputHelper}>
                      Sin costo cargado todavía. El margen y la sugerencia necesitan un costo.
                    </Text>
                  ) : null}
                </View>
              ) : editingProductCost == null ? (
                <Text style={styles.inputHelper}>
                  Sin costo cargado en esta sucursal todavía. El margen y la sugerencia
                  necesitan un costo.
                </Text>
              ) : null}
              {editingProductCost != null ? (
                <>
                  <View style={styles.ticketFormField}>
                    <Text style={styles.ticketFormLabel}>Margen objetivo %</Text>
                    <NumericTextInput
                      kind="percent"
                      style={styles.ticketFormInput}
                      value={priceMarginPercentInput}
                      onChangeText={onPriceMarginPercentChange}
                      placeholder="Ej: 35"
                      placeholderTextColor={ticketColors.ink3}
                    />
                    <Text style={styles.inputHelper}>
                      La sugerencia usa el costo promedio y calcula el precio para llegar a ese margen.
                    </Text>
                  </View>
                  <View style={styles.priceGuideActionRow}>
                    <View style={styles.priceGuideSuggestedBox}>
                      <Text style={styles.priceGuideSuggestedLabel}>Precio sugerido</Text>
                      <Text style={styles.priceGuideSuggestedValue}>
                        {editingProductSuggestedPrice != null
                          ? currencyFormatter.format(editingProductSuggestedPrice)
                          : '—'}
                      </Text>
                    </View>
                    <TouchableOpacity
                      style={[
                        posUi.primaryButton,
                        styles.suggestButton,
                        editingProductSuggestedPrice == null && posUi.disabledCard,
                      ]}
                      onPress={onApplySuggestedPrice}
                      disabled={editingProductSuggestedPrice == null}
                      accessibilityRole="button"
                      accessibilityLabel="Usar precio sugerido"
                    >
                      <Text style={posUi.primaryButtonText}>Usar sugerencia</Text>
                    </TouchableOpacity>
                  </View>
                </>
              ) : null}
            </>
          )}
        </View>
      ) : null}
    </View>
  );
}
